/**
 * Split one MJPEG byte stream (`-vf fps=N ... -f mjpeg`, `replay-decode.ts`'s
 * single ffmpeg invocation) into its N JPEG frames.
 *
 * One decode, not N: a 10 s clip at 4 fps is 40 frames, and 40 spawns would
 * be 40 seeks on a node that also decodes live streams — the difference
 * between "a run costs a second" and "a run costs a minute". This module is
 * the OTHER half of that choice: turning the single stream back into
 * individually addressable frames.
 *
 * Pure byte scanning on the JPEG SOI/EOI markers (`0xFFD8` / `0xFFD9`) — no
 * dependency on a JPEG decoder, since the goal is only to find frame
 * boundaries, not to interpret pixels.
 */

const SOI = Buffer.from([0xff, 0xd8])
const EOI = Buffer.from([0xff, 0xd9])

/**
 * Split a concatenated MJPEG byte stream into its frames, in order.
 *
 * A frame missing its EOI (an ffmpeg killed mid-write leaves a headless
 * tail) is DISCARDED rather than delivered half-formed — consuming a
 * truncated JPEG downstream would fail far from this function, and the
 * frame count here is the only thing that makes the loss visible.
 */
export function splitMjpegStream(bytes: Uint8Array): readonly Buffer[] {
  const buf = Buffer.isBuffer(bytes)
    ? bytes
    : Buffer.from(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const out: Buffer[] = []
  let pos = 0
  for (;;) {
    const soi = buf.indexOf(SOI, pos)
    if (soi === -1) break
    const eoi = buf.indexOf(EOI, soi + SOI.length)
    if (eoi === -1) break // truncated tail — discard, don't deliver half a frame
    out.push(buf.subarray(soi, eoi + EOI.length))
    pos = eoi + EOI.length
  }
  return out
}
